import './map-settings.css';

import h from 'hyperscript';
import close from '@disy/cadenza-icons/close.svg';

import { createIconButton } from 'ui/button/button';

import { TogglePanelSection } from 'cadenza/map/map-settings/toggle-panel-section/toggle-panel-section';
import { icon } from 'cadenza/utils/icon/icon';
import { MapProperty } from 'cadenza/map/map-property';
import { getUrlParam, hasParameter } from 'cadenza/utils/url-param-utils';
import {
  BlockIdentifier,
  getBlockSettingsSingleton
} from 'cadenza/map/block-settings';

import i18n from './map-settings.properties';

const NAME = 'd-map-settings';

export class MapSettings extends HTMLElement {

  #config;
  #map;
  #content;
  #blockSettings;

  constructor (config, map) {
    super();
    this.#config = config;
    this.#map = map;
    this.#blockSettings = getBlockSettingsSingleton();

    this._addHeading();
    this.append(this.#content = h(`.${NAME}--content`));
    this._addOverviewMapSection(i18n('overviewMap'), BlockIdentifier.OVERVIEW_MAP);
    this._addStatusBarSection(i18n('statusBarControls'));
    this._addCompactLegendSection(i18n('compactLegend'), BlockIdentifier.COMPACT_LEGEND);

    this._updateVisibility();
    map.on(`change:${MapProperty.SETTINGS_VISIBLE}`, () => this._updateVisibility());
  }

  connectedCallback () {
    this.classList.add(NAME);
  }

  _addHeading () {
    this.append(h('.d-compact-header', [
      h('.d-compact-header--title', i18n('settings')),
      createIconButton(icon(close), i18n('common:close'), {
        variant: 'borderless',
        size: 's',
        onclick: () => this._onCloseClick()
      })
    ]));
  }

  _onCloseClick () {
    this.#map.set(MapProperty.SETTINGS_VISIBLE, false);
  }

  _updateVisibility () {
    const hidden = !this.#map.get(MapProperty.SETTINGS_VISIBLE);
    if (hidden !== this.hidden) {
      this.hidden = hidden;
      this.#map.updateSize();
    }
  }

  _addCompactLegendSection (label, blockIdentifier) {
    const section = new TogglePanelSection(
      label,
      this.#blockSettings.isBlockVisible(blockIdentifier, !this.#config.controls?.compactLegend?.isMinimized),
      event => this.#blockSettings.setVisibility(blockIdentifier, event.detail.checked ? 'regular' : 'minimized')
    );
    section.checked = this.#blockSettings.isBlockVisible(blockIdentifier);
    this.#blockSettings.on(`change:${blockIdentifier}`, () => { section.checked = this.#blockSettings.isBlockVisible(blockIdentifier); });
    this.#content.append(section);
  }

  _isOverviewMapDynamic () {
    // determine first from URL for browser-tab persistence,
    // if that is not set, use mml-persistence.
    const dynamicExtent = MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT;
    if (hasParameter(dynamicExtent)) {
      return getUrlParam({ name: dynamicExtent }) === 'true';
    }
    return this.#map.get(dynamicExtent) === true;
  }

  _addOverviewMapSection (label, blockIdentifier) {
    const section = new TogglePanelSection(
      label,
      this.#blockSettings.isBlockVisible(blockIdentifier, !this.#config.controls?.overviewMap?.isMinimized),
      event => this.#blockSettings.setVisibility(blockIdentifier, event.detail.checked ? 'regular' : 'minimized'));
    [
      this._createMapPropertySubToggle(i18n('overviewMapDynamicExtent'), MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT, 'd-overview-map-dynamic-extent-toggle', { checked: this._isOverviewMapDynamic() }),
      this._createMapPropertySubToggle(i18n('overviewMapAspectRatioIncluded'), MapProperty.OVERVIEW_MAP_DYNAMIC_ASPECT_RATIO, 'd-overview-map-aspect-ratio-toggle')
    ].forEach(element => {
      const subtoggle = section.addSubToggle(element.label, element.checked, element.onToggle, element.className);
      subtoggle.hidden = !this.#map.get(element.mapProperty);

      this.#map.on(`change:${element.mapProperty}`, () => {
        section.setSubToggleChecked(this.#map.get(element.mapProperty), element.className);
      });
    });
    this.#blockSettings.on(`change:${blockIdentifier}`, () => {
      const isVisible = this.#blockSettings.isBlockVisible(blockIdentifier);
      section.checked = isVisible;
      section.querySelectorAll('.d-panel-section--subtoggle').forEach(element => {
        element.hidden = !isVisible;
      });
    });

    this.#content.append(section);
  }

  _addStatusBarSection (label) {
    const section = new TogglePanelSection(label);
    section.addSubDescription(i18n('statusBarDescription'));
    [
      this._createBlockSettingSubToggle(
        i18n('scaleLine'),
        BlockIdentifier.SCALE_LINE,
        'd-scale-line-toggle',
        this.#blockSettings.isBlockVisible(BlockIdentifier.SCALE_LINE, this.#config.controls.scaleLine)
      ),
      this._createBlockSettingSubToggle(
        i18n('projection'),
        BlockIdentifier.PROJECTION_DESCRIPTION,
        'd-projection-toggle',
        this.#blockSettings.isBlockVisible(BlockIdentifier.PROJECTION_DESCRIPTION, this.#config.controls.projectionDescription)),
      this._createBlockSettingSubToggle(
        i18n('scaleInput'),
        BlockIdentifier.SCALE_INPUT,
        'd-scale-input-toggle',
        this.#blockSettings.isBlockVisible(BlockIdentifier.SCALE_INPUT, this.#config.controls.scaleInput))
    ].forEach(element => {
      section.addSubToggle(element.label, element.checked, element.onToggle, element.className);
      this.#blockSettings.on(`change:${element.blockIdentifier}`, () => {
        const vi = this.#blockSettings.isBlockVisible(element.blockIdentifier);
        section.setSubToggleChecked(vi, element.className);
      });
    });
    this.#content.append(section);
  }

  _createMapPropertySubToggle (label, mapProperty, className, { checked = this.#map.get(mapProperty) } = {}) {
    return {
      label,
      checked,
      onToggle: event => {
        this.#map.set(mapProperty, event.detail.checked);
      },
      className,
      mapProperty
    };
  }

  _createBlockSettingSubToggle (label, blockIdentifier, className, checked) {
    return {
      label,
      checked,
      onToggle: event => {
        this.#blockSettings.setVisibility(blockIdentifier, event.detail.checked ? 'regular' : 'minimized');
      },
      className,
      blockIdentifier
    };
  }

}

customElements.define(NAME, MapSettings);
